import { useEffect, useState } from "react";
import { api, fmtINR } from "../../lib/api";
import { StoreLayout } from "../../components/StoreLayout";
import { useCart } from "../../context/CartContext";
import { useLocationCtx } from "../../context/LocationContext";
import { ServiceabilityGate } from "../../components/store/ServiceabilityGate";
import { FaqSection } from "../../components/store/FaqSection";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Check, Star, ShoppingCart, ArrowRight, ShieldCheck, CaretDown, X, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { categoryImage, mrp, discountPct } from "../../lib/storefront";

export default function Services() {
  const [catalog, setCatalog] = useState([]);
  const [params] = useSearchParams();
  const activeCat = params.get("category") || "";
  const q = (params.get("q") || "").trim().toLowerCase();
  const { add, has, items } = useCart();
  const { loc } = useLocationCtx();
  const navigate = useNavigate();
  const [covers, setCovers] = useState(null); // { service_type, data }
  const [coversBusy, setCoversBusy] = useState(false);
  const [gate, setGate] = useState(null); // { category, action }

  // Ensure the selected pincode covers this category before proceeding.
  const ensureServiceable = async (category, action) => {
    if (loc?.pincode) {
      try {
        const { data } = await api.get("/public/check-service", { params: { pincode: loc.pincode, category } });
        if (data.available) { action(loc.pincode); return; }
      } catch (e) {}
    }
    setGate({ category, action });
  };

  const openCovers = async (serviceType) => {
    setCoversBusy(true); setCovers({ service_type: serviceType, data: null });
    try {
      const { data } = await api.get(`/public/service-details/${encodeURIComponent(serviceType)}`);
      setCovers({ service_type: serviceType, data });
    } catch { setCovers({ service_type: serviceType, data: { inclusions: [], exclusions: [], notes: [] } }); }
    setCoversBusy(false);
  };

  useEffect(() => { api.get("/public/services").then((r) => setCatalog(r.data)); }, []);

  let shown = activeCat ? catalog.filter((c) => c.category === activeCat) : catalog;
  if (q) {
    shown = catalog
      .map((c) => {
        const catMatch = c.category.toLowerCase().includes(q);
        const product_types = c.product_types
          .map((pt) => {
            const ptMatch = pt.product_type.toLowerCase().includes(q);
            const services = (catMatch || ptMatch)
              ? pt.services
              : pt.services.filter((s) =>
                  (s.service_type || "").toLowerCase().includes(q) ||
                  (s.part_name || "").toLowerCase().includes(q));
            return { ...pt, services };
          })
          .filter((pt) => pt.services.length > 0);
        return { ...c, product_types };
      })
      .filter((c) => c.product_types.length > 0);
  }

  const onAdd = (cat, pt, s) => {
    const item = { product_category: cat, product_type: pt, service_type: s.service_type, part_name: s.part_name || "", amount: s.amount, payment_mode: s.payment_mode || "full", booking_amount: s.booking_amount || 0 };
    add(item);
    toast.success(`${s.service_type} added to cart`);
  };

  return (
    <StoreLayout>
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 py-10 md:py-12">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Book a service</p>
          <h1 className="font-[Outfit] text-4xl md:text-5xl font-extrabold tracking-tight mt-2">{q ? `Results for "${q}"` : (activeCat || "All Services")}</h1>
          <p className="text-[#475569] mt-2 flex items-center gap-2"><ShieldCheck size={16} weight="fill" className="text-[#166534]" /> Transparent pricing · Verified pros · Pay after the service.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 py-10">
        {/* category filter pills */}
        <div className="flex flex-wrap gap-2 mb-9" data-testid="category-filter">
          <button onClick={() => navigate("/services")}
            className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors duration-200 ${!activeCat ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#E2E8F0] bg-white hover:border-[#4F46E5]"}`}>
            All
          </button>
          {catalog.map((c) => (
            <button key={c.category} onClick={() => navigate(`/services?category=${encodeURIComponent(c.category)}`)}
              data-testid={`filter-${c.category.replace(/ /g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors duration-200 ${activeCat === c.category ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#E2E8F0] bg-white hover:border-[#4F46E5]"}`}>
              {c.category}
            </button>
          ))}
        </div>

        <div className="space-y-12" data-testid="services-list">
          {shown.length === 0 && <p className="text-[#475569]">No services available right now.</p>}
          {shown.map((c) => (
            <div key={c.category}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0">
                  <img src={categoryImage(c.category)} alt={c.category} className="w-full h-full object-cover" />
                </div>
                <h2 className="font-[Outfit] text-2xl md:text-3xl font-bold tracking-tight">{c.category}</h2>
              </div>
              {c.product_types.map((pt) => (
                <div key={pt.product_type} className="mb-7">
                  <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#475569] mb-3">{pt.product_type}</p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {pt.services.map((s, i) => {
                      const item = { product_category: c.category, product_type: pt.product_type, service_type: s.service_type, part_name: s.part_name || "", amount: s.amount };
                      const inCart = has(item);
                      const off = discountPct(s.amount);
                      return (
                        <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-3 flex gap-4 hover:shadow-md transition-shadow duration-200" data-testid={`svc-${c.category.replace(/ /g,"-")}-${i}`}>
                          <div className="relative w-28 h-28 rounded-xl overflow-hidden shrink-0">
                            <img src={categoryImage(c.category)} alt={s.service_type} loading="lazy" className="w-full h-full object-cover" />
                            {off > 0 && <span className="absolute top-1.5 left-1.5 bg-[#166534] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{off}% OFF</span>}
                          </div>
                          <div className="flex-1 flex flex-col min-w-0">
                            <h3 className="font-[Outfit] font-bold flex items-center gap-1.5">
                              {s.featured && <Star size={13} weight="fill" className="text-[#4F46E5]" />}
                              {s.service_type}
                            </h3>
                            {s.part_name && <p className="text-xs text-[#475569] mt-0.5">{s.part_name}</p>}
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-[#166534] font-medium"><ShieldCheck size={13} weight="fill" /> Fixigo Guarantee</div>
                            <button onClick={() => openCovers(s.service_type)} data-testid={`covers-${c.category.replace(/ /g,"-")}-${i}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-[#4F46E5] hover:underline mt-1 w-fit">
                              <Info size={13} weight="bold" /> What's covered
                            </button>
                            <div className="mt-auto flex items-center justify-between pt-3">
                              <div>
                                <span className="font-[Outfit] font-extrabold text-lg">{fmtINR(s.amount)}</span>
                                {off > 0 && <span className="text-xs text-[#94a3b8] line-through ml-1.5">{fmtINR(mrp(s.amount))}</span>}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => ensureServiceable(c.category, (pin) => navigate(`/book?category=${encodeURIComponent(c.category)}&type=${encodeURIComponent(pt.product_type)}&service=${encodeURIComponent(s.service_type)}&amount=${s.amount}&pincode=${pin || ""}&pay=${s.payment_mode || "full"}&booking=${s.booking_amount || 0}`))}
                                  data-testid={`book-${c.category.replace(/ /g,"-")}-${i}`}
                                  className="rounded-xl font-bold border-[#4F46E5] text-[#4F46E5] hover:bg-[#4F46E5]/10">Book Now</Button>
                                <Button size="sm" onClick={() => ensureServiceable(c.category, () => onAdd(c.category, pt.product_type, s))} disabled={inCart}
                                  data-testid={`add-${c.category.replace(/ /g,"-")}-${i}`}
                                  className={`rounded-xl gap-1.5 font-bold ${inCart ? "bg-[#166534] hover:bg-[#166534] text-white" : "bg-[#4F46E5] hover:bg-[#4338CA] text-white"}`}>
                                  {inCart ? <><Check size={15} weight="bold" /> Added</> : <><ShoppingCart size={15} weight="bold" /> Add</>}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {items.length > 0 && (
        <div className="sticky bottom-0 bg-white/90 backdrop-blur-xl border-t border-[#E2E8F0] z-40" data-testid="cart-bar">
          <div className="max-w-7xl mx-auto px-5 py-3.5 flex items-center justify-between">
            <span className="text-sm font-bold">{items.length} item{items.length > 1 ? "s" : ""} in cart</span>
            <Button onClick={() => navigate("/cart")} className="rounded-xl gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" data-testid="goto-cart">
              View Cart <ArrowRight size={16} weight="bold" />
            </Button>
          </div>
        </div>
      )}
      {covers && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" onClick={() => setCovers(null)} data-testid="covers-modal">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
              <h3 className="font-[Outfit] text-xl font-bold">{covers.service_type} — what's covered</h3>
              <button onClick={() => setCovers(null)} className="text-[#94a3b8] hover:text-[#1E293B]" data-testid="covers-close"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-6">
              {coversBusy && <p className="text-sm text-[#475569]">Loading…</p>}
              {!coversBusy && covers.data && (() => {
                const { inclusions = [], exclusions = [], notes = [] } = covers.data;
                if (inclusions.length + exclusions.length + notes.length === 0)
                  return <p className="text-sm text-[#475569]">Details for this service will be available soon.</p>;
                return (
                  <>
                    {inclusions.length > 0 && (
                      <div data-testid="covers-included">
                        <p className="font-[Outfit] font-bold text-[#166534] flex items-center gap-1.5 mb-2"><Check size={16} weight="bold" /> What's Included</p>
                        <ul className="space-y-2">{inclusions.map((it, k) => (
                          <li key={k} className="flex items-start gap-2 text-sm"><Check size={15} weight="bold" className="text-[#166534] mt-0.5 shrink-0" /><span>{it.text}</span></li>
                        ))}</ul>
                      </div>
                    )}
                    {exclusions.length > 0 && (
                      <div data-testid="covers-excluded">
                        <p className="font-[Outfit] font-bold text-[#b91c1c] flex items-center gap-1.5 mb-2"><X size={16} weight="bold" /> What's Excluded</p>
                        <ul className="space-y-2">{exclusions.map((it, k) => (
                          <li key={k} className="flex items-start gap-2 text-sm"><X size={15} weight="bold" className="text-[#b91c1c] mt-0.5 shrink-0" /><span>{it.text}</span></li>
                        ))}</ul>
                      </div>
                    )}
                    {notes.length > 0 && (
                      <div data-testid="covers-notes">
                        <p className="font-[Outfit] font-bold text-[#b45309] flex items-center gap-1.5 mb-2"><Info size={16} weight="bold" /> Good to Know</p>
                        <ul className="space-y-2">{notes.map((it, k) => (
                          <li key={k} className="flex items-start gap-2 text-sm"><Info size={15} weight="bold" className="text-[#b45309] mt-0.5 shrink-0" /><span>{it.text}</span></li>
                        ))}</ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {gate && (
        <ServiceabilityGate
          open={!!gate}
          category={gate.category}
          defaultPincode={loc?.pincode || ""}
          onClose={() => setGate(null)}
          onServiceable={(pin) => { const a = gate.action; setGate(null); a && a(pin); }}
        />
      )}
      <div className="max-w-6xl mx-auto px-4">
        <FaqSection category={activeCat} />
      </div>
    </StoreLayout>
  );
}
